import logger from "@/utils/logger.js";
import axios from "axios";
import {checkToken} from '@/utils/lambdaUtils';
import {useAuthStore} from '@/stores/authStore';
import {AdminUserEntityModel} from "@/models/AdminUserEntityModel.js";
import api from "@/services/api.js";
import {AdminUserModel} from "@/models/AdminUserModel.js";
import {AdminExternalAppealModel} from "@/models/AdminExternalAppealModel.js";
import {AdminConsumerComplaintModel} from "@/models/AdminConsumerComplaintModel.js";
import {UserModel} from "@/models/UserModel.js";

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL + '/admin/';

export const getAllUsers = async () => {
    try {
        await checkToken();
        const store = useAuthStore();
        const response = await axios.get(`${baseUrl}get_all_users`, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        if (Array.isArray(response.data)) {
            return response.data.map(item => new UserModel(item));
        } else {
            throw new Error('Invalid response data format');
        }
    } catch (error) {
        logger.error('Error getting all user list', error);
    }
};

export const getAdminUsers = async () => {
    try {
        await checkToken();
        const store = useAuthStore();
        const response = await axios.get(`${baseUrl}get_admin_users`, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        if (Array.isArray(response.data)) {
            return response.data.map(item => new AdminUserModel(item));
        } else {
            throw new Error('Invalid response data format');
        }
    } catch (error) {
        logger.error('Error getting admin user list', error);
    }
};

export const setAdminCanLogin = async (id, canLogin) => {
    const numId = Number.parseInt(id, 10);
    if (!numId) {
        throw new Error('Missing required parameters');
    }

    try {
        await checkToken();
        const response = await api.post(`${baseUrl}update_admin_user_can_login?id=${encodeURIComponent(numId)}&canLogin=${encodeURIComponent(canLogin)}`);
        return response.data;
    } catch (error) {
        logger.error('Error modifying user canLogin flag', error);
        throw error;
    }
};

export const getAdminUserEntities = async () => {
    try {
        await checkToken();
        const store = useAuthStore();
        const response = await axios.get(`${baseUrl}get_admin_user_entities`, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        if (Array.isArray(response.data)) {
            return response.data.map(item => new AdminUserEntityModel(item));
        } else {
            throw new Error('Invalid response data format');
        }
    } catch (error) {
        logger.error('Error getting admin user entity list', error);
    }
};

export const deleteAdminUserEntity = async (id) => {
    const numId = Number.parseInt(id, 10);
    if (!numId) {
        throw new Error('Missing required parameters');
    }

    try {
        await checkToken();
        const response = await api.post(`${baseUrl}delete_admin_user_entity?id=${encodeURIComponent(numId)}`);
        return response.data;
    } catch (error) {
        logger.error('Error deleting user entity', error);
        throw error;
    }
};

export const getAdminExternalAppeals = async () => {
    try {
        await checkToken();
        const store = useAuthStore();
        const response = await axios.get(`${baseUrl}get_admin_external_appeals`, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        if (Array.isArray(response.data)) {
            return response.data.map(item => new AdminExternalAppealModel(item));
        } else {
            throw new Error('Invalid response data format');
        }
    } catch (error) {
        logger.error('Error getting external appeals', error);
    }
};

export const getAdminConsumerComplaints = async () => {
    try {
        await checkToken();
        const store = useAuthStore();
        const response = await axios.get(`${baseUrl}get_admin_consumer_complaints`, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        if (Array.isArray(response.data)) {
            return response.data.map(item => new AdminConsumerComplaintModel(item));
        } else {
            throw new Error('Invalid response data format');
        }
    } catch (error) {
        logger.error('Error getting admin consumer complaints', error);
    }
};

export const updateAdminExternalAppeal = async (adminExternalAppeal) => {
    if (!adminExternalAppeal) {
        throw new Error('Missing required parameters');
    }

    try {
        await checkToken();
        const store = useAuthStore();
        const response = await api.post(`${baseUrl}update_admin_external_appeal`, adminExternalAppeal, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        return response.data;
    } catch (error) {
        logger.error('Error updating external appeal', error);
        throw error;
    }
};

export const updateAdminConsumerComplaint = async (adminConsumerComplaint) => {
    if (!adminConsumerComplaint) {
        throw new Error('Missing required parameters');
    }

    try {
        await checkToken();
        const store = useAuthStore();
        const response = await api.post(`${baseUrl}update_admin_consumer_complaint`, adminConsumerComplaint, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        return response.data;
    } catch (error) {
        logger.error('Error updating consumer complaint', error);
        throw error;
    }
};

export const addExternalAppealDocument = async (appealId, type, url) => {
    if (!appealId || !type || !url) {
        throw new Error('Missing required parameters');
    }

    try {
        await checkToken();
        const response = await api.post(
            `${baseUrl}add_external_appeal_document`,
            {
                appealId,
                type,
                url
            }
        );
        return response.data;
    } catch (error) {
        logger.error('Error adding document to external appeal', error);
        throw error;
    }
};

export const addConsumerComplaintDocument = async (complaintId,type, url) => {
    if (!complaintId || !type || !url) {
        throw new Error('Missing required parameters');
    }

    try {
        await checkToken();
        const response = await api.post(
            `${baseUrl}add_consumer_complaint_document`,
            {
                complaintId,
                type,
                url
            }
        );
        return response.data;
    } catch (error) {
        logger.error('Error adding document to consumer complaint', error);
        throw error;
    }
};
